import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import {
    FaBalanceScale, FaMicrochip, FaSync, FaTimes, FaCheckCircle,
    FaExclamationTriangle, FaChevronRight, FaPrint, FaRobot,
    FaCalculator, FaShieldAlt, FaArrowUp, FaCheckDouble,
    FaChartPie, FaUserEdit,
} from 'react-icons/fa';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { toast } from 'react-toastify';
import './BalancoPatrimonial.css';

// =============================================================================
// HELPERS
// =============================================================================
const fmt = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const fmtK = (v) => {
    const abs = Math.abs(v || 0);
    if (abs >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000)     return `R$ ${(v / 1_000).toFixed(1)}k`;
    return fmt(v);
};

const ANOS = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

const PIE_COLORS = ['#3B82F6', '#EF4444', '#10B981'];

// =============================================================================
// PRINT — documento de qualidade consultoria externa
// =============================================================================
const handlePrint = (dados, ano) => {
    if (!dados) { toast.warn('Nenhum dado para imprimir.'); return; }

    const { ativos, passivos, patrimonio_liquido: pl, resumo } = dados;
    const diff        = resumo.totalAtivo - (resumo.totalPassivo + resumo.totalPL);
    const equilibrado = Math.abs(diff) < 1;
    const liquidez    = resumo.totalPassivo > 0 ? (resumo.totalAtivo / resumo.totalPassivo).toFixed(2) : '—';
    const hoje        = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

    const grupoHTML = (titulo, items, total, cor) => {
        const linhas = (items || []).map(i =>
            `<tr><td style="padding:6px 12px 6px 24px;font-size:12.5px;color:#374151;">
                ${i.origem === 'calculado' ? '<em>' : ''}${i.conta}${i.origem === 'calculado' ? '</em>' : ''}
             </td>
             <td style="padding:6px 12px;text-align:right;font-weight:600;font-size:12.5px;color:#1e293b;">${fmt(i.valor)}</td></tr>`
        ).join('');
        return `
        <div style="margin-bottom:20px;">
            <div style="padding:8px 12px;background:${cor};color:white;font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.12em;border-radius:4px 4px 0 0;">${titulo}</div>
            <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 4px 4px;">
                <tbody>${linhas}</tbody>
                <tfoot><tr style="background:#f8fafc;border-top:2px solid #e5e7eb;">
                    <td style="padding:8px 12px;font-weight:800;font-size:13px;">Total</td>
                    <td style="padding:8px 12px;text-align:right;font-weight:800;font-size:13px;color:${cor};">${fmt(total)}</td>
                </tr></tfoot>
            </table>
        </div>`;
    };

    const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="UTF-8">
<title>Balanço Patrimonial ${ano}</title>
<style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Source+Serif+4:wght@400;600&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    body{font-family:'Source Serif 4',Georgia,serif;background:white;color:#0f172a;font-size:13px;}
    .cover{background:#0B1220;padding:36px 48px 28px;display:flex;justify-content:space-between;align-items:flex-end;}
    .cover-title{font-family:'Playfair Display',serif;font-size:26px;font-weight:900;color:white;letter-spacing:-.02em;line-height:1.15;}
    .cover-sub{font-size:11.5px;color:#94a3b8;margin-top:4px;letter-spacing:.04em;text-transform:uppercase;}
    .cover-meta{text-align:right;font-size:11px;color:#64748b;}
    .cover-year{font-family:'Playfair Display',serif;font-size:48px;font-weight:900;color:#C9A84C;line-height:1;}
    .kpi-bar{display:flex;border-bottom:3px solid #0B1220;}
    .kpi{flex:1;padding:14px 20px;border-right:1px solid #e5e7eb;}
    .kpi:last-child{border-right:none;}
    .kpi-lbl{font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.1em;color:#94a3b8;margin-bottom:5px;}
    .kpi-val{font-size:17px;font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-.02em;}
    .kpi-sub{font-size:10px;color:#64748b;margin-top:2px;}
    .body{display:grid;grid-template-columns:1fr 1fr;gap:28px;padding:28px 48px;}
    .col-title{font-family:'Playfair Display',serif;font-size:16px;font-weight:700;color:#0B1220;border-bottom:2px solid #0B1220;padding-bottom:8px;margin-bottom:16px;text-transform:uppercase;letter-spacing:.06em;}
    .status{margin:0 48px 28px;padding:12px 20px;text-align:center;font-weight:800;font-size:13px;border-radius:6px;background:${equilibrado?'#dcfce7':'#fee2e2'};color:${equilibrado?'#166534':'#991b1b'};}
    .footer{margin:0 48px 28px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8;}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;} .cover{-webkit-print-color-adjust:exact;}}
</style></head><body>
<div class="cover">
    <div>
        <div class="cover-title">Balanço Patrimonial</div>
        <div class="cover-sub">Demonstração Consolidada • BLB System ERP</div>
    </div>
    <div style="text-align:right;">
        <div class="cover-year">${ano}</div>
        <div class="cover-meta">Exercício Social<br>Emitido em ${hoje}</div>
    </div>
</div>

<div class="kpi-bar">
    <div class="kpi">
        <div class="kpi-lbl">Ativo Total</div>
        <div class="kpi-val" style="color:#3B82F6;">${fmt(resumo.totalAtivo)}</div>
        <div class="kpi-sub">Bens e Direitos</div>
    </div>
    <div class="kpi">
        <div class="kpi-lbl">Passivo Total</div>
        <div class="kpi-val" style="color:#EF4444;">${fmt(resumo.totalPassivo)}</div>
        <div class="kpi-sub">Obrigações</div>
    </div>
    <div class="kpi">
        <div class="kpi-lbl">Patrimônio Líquido</div>
        <div class="kpi-val" style="color:#10B981;">${fmt(resumo.totalPL)}</div>
        <div class="kpi-sub">Riqueza Líquida</div>
    </div>
    <div class="kpi">
        <div class="kpi-lbl">Resultado do Exercício</div>
        <div class="kpi-val" style="color:${resumo.resultadoExercicio>=0?'#10B981':'#EF4444'};">${fmt(resumo.resultadoExercicio)}</div>
        <div class="kpi-sub">${resumo.resultadoExercicio>=0?'Lucro Líquido':'Prejuízo Líquido'}</div>
    </div>
    <div class="kpi">
        <div class="kpi-lbl">Liquidez Geral</div>
        <div class="kpi-val" style="color:#F59E0B;">${liquidez}x</div>
        <div class="kpi-sub">A/P · CPC 26</div>
    </div>
    <div class="kpi">
        <div class="kpi-lbl">Equilíbrio</div>
        <div class="kpi-val" style="color:${equilibrado?'#10B981':'#EF4444'};">${equilibrado?'ESTÁVEL':'DIVERGENTE'}</div>
        <div class="kpi-sub">${equilibrado?'Partidas dobradas OK':'Diferença: '+fmt(diff)}</div>
    </div>
</div>

<div class="body">
    <div>
        <div class="col-title">1. Ativos</div>
        ${grupoHTML('1.1 Ativo Circulante', ativos.circulante.detalhes, ativos.circulante.total, '#3B82F6')}
        ${grupoHTML('1.2 Ativo Não Circulante', ativos.nao_circulante.detalhes, ativos.nao_circulante.total, '#1D4ED8')}
        <div style="display:flex;justify-content:space-between;padding:10px 12px;background:#0B1220;color:white;border-radius:4px;font-weight:800;font-size:14px;">
            <span>TOTAL DO ATIVO</span><span>${fmt(resumo.totalAtivo)}</span>
        </div>
    </div>
    <div>
        <div class="col-title">2. Passivos e PL</div>
        ${grupoHTML('2.1 Passivo Circulante', passivos.circulante.detalhes, passivos.circulante.total, '#EF4444')}
        ${grupoHTML('2.2 Passivo Não Circulante', passivos.nao_circulante.detalhes, passivos.nao_circulante.total, '#B91C1C')}
        ${grupoHTML('3. Patrimônio Líquido', pl.detalhes, pl.total, '#10B981')}
        <div style="display:flex;justify-content:space-between;padding:10px 12px;background:#0B1220;color:white;border-radius:4px;font-weight:800;font-size:14px;">
            <span>TOTAL PASSIVO + PL</span><span>${fmt(resumo.totalPassivo + resumo.totalPL)}</span>
        </div>
    </div>
</div>

<div class="status">
    ${equilibrado
        ? '✓ Balanço Patrimonial Equilibrado — Equação Contábil Ativo = Passivo + PL verificada.'
        : `⚠ DIVERGÊNCIA PATRIMONIAL de ${fmt(Math.abs(diff))} detectada. Verifique lançamentos sem classificação contábil.`}
</div>

${dados.conclusao_ia ? `
<div style="margin:0 48px 28px;padding:20px 24px;background:#f8fafc;border-left:4px solid #6366f1;border-radius:0 6px 6px 0;">
    <div style="font-size:9.5px;font-weight:800;text-transform:uppercase;letter-spacing:.1em;color:#6366f1;margin-bottom:10px;">Parecer Gemini IA</div>
    <div style="font-size:12.5px;line-height:1.7;color:#334155;">${dados.conclusao_ia}</div>
</div>` : ''}

<div class="footer">
    <span>BLB System ERP · Módulo Financeiro Contábil</span>
    <span>Elaborado em ${hoje} · Uso exclusivo interno / externo mediante assinatura digital</span>
</div>
<script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};<\/script>
</body></html>`;

    const w = window.open('', '_blank', 'width=1100,height=820');
    if (w) { w.document.write(html); w.document.close(); }
    else toast.warn('Popup bloqueado. Permita popups e tente novamente.');
};

// =============================================================================
// SUB-COMPONENTES
// =============================================================================
const KPICard = ({ icon, title, value, sub, type }) => (
    <div className={`bp-kpi-card bp-kpi-${type}`}>
        <div className="bp-kpi-icon">{icon}</div>
        <div className="bp-kpi-body">
            <div className="bp-kpi-title">{title}</div>
            <div className="bp-kpi-value">{value}</div>
            {sub && <div className="bp-kpi-sub">{sub}</div>}
        </div>
    </div>
);

const AccountGroup = ({ title, colorKey, total, items = [], fmt: f }) => (
    <div className={`bp-account-group bp-group-${colorKey}`}>
        <div className="bp-group-header">
            <span className="bp-group-title">{title}</span>
            <span className="bp-group-total">{f(total)}</span>
        </div>
        <ul className="bp-account-list">
            {items.length > 0 ? items.map((acc, i) => (
                <li key={i} className={`bp-account-item ${acc.destaque ? 'bp-item-highlight' : ''}`}>
                    <span className="bp-item-name">
                        {acc.automatico
                            ? <FaRobot className="bp-icon-ia" />
                            : <FaChevronRight className="bp-icon-mini" />}
                        <span className={acc.positivo === false ? 'bp-text-danger' : acc.positivo === true ? 'bp-text-success' : ''}>
                            {acc.conta}
                        </span>
                    </span>
                    <span className="bp-item-val">{f(acc.valor)}</span>
                </li>
            )) : (
                <li className="bp-account-empty">Nenhum lançamento classificado</li>
            )}
        </ul>
    </div>
);

const SectionHeader = ({ code, title, total, variant, fmt: f }) => (
    <div className={`bp-section-header bp-section-${variant}`}>
        <div className="bp-section-code">{code}</div>
        <div className="bp-section-label">{title}</div>
        <div className="bp-section-total">{f(total)}</div>
    </div>
);

const GrandTotal = ({ label, value, fmt: f, variant }) => (
    <div className={`bp-grand-total bp-grand-${variant}`}>
        <span>{label}</span>
        <span>{f(value)}</span>
    </div>
);

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================
export default function BalancoPatrimonial() {
    const [dados, setDados]             = useState(null);
    const [loading, setLoading]         = useState(true);
    const [iaLoading, setIaLoading]     = useState(false);
    const [ano, setAno]                 = useState(new Date().getFullYear());
    const [iaAtiva, setIaAtiva]         = useState(false);
    const [showIaParecer, setShowIaParecer] = useState(false);

    const fetchData = useCallback(async (comIA = false) => {
        if (comIA) setIaLoading(true);
        else       setLoading(true);

        try {
            let response;
            if (comIA) {
                // ROTA CORRIGIDA: /api/financeiro/balanco/consolidar-ia
                response = await api.post('/api/financeiro/balanco/consolidar-ia', { ano });
                setIaAtiva(true);
                if (response.data.conclusao_ia) setShowIaParecer(true);
                toast.success('Auditoria Gemini concluída!');
            } else {
                // ROTA CORRIGIDA: /api/financeiro/balanco-consolidado
                response = await api.get('/api/financeiro/balanco-consolidado', { params: { ano } });
                setIaAtiva(false);
            }
            setDados(response.data);
        } catch (err) {
            console.error('Erro no BP:', err);
            const msg = err.response?.status === 404
                ? 'Rota não encontrada. Verifique o backend.'
                : 'Falha ao carregar Balanço Patrimonial.';
            toast.error(msg);
        } finally {
            setLoading(false);
            setIaLoading(false);
        }
    }, [ano]);

    useEffect(() => { fetchData(false); }, [fetchData]);

    const stats = useMemo(() => {
        if (!dados) return null;
        const { resumo, ativos, passivos, patrimonio_liquido: pl } = dados;
        const totalPassivoCirc = passivos?.circulante?.total || 0;
        const totalAtivoCirc   = ativos?.circulante?.total   || 0;
        const liquidez = totalPassivoCirc > 0 ? (totalAtivoCirc / totalPassivoCirc) : 0;
        const diff     = resumo.totalAtivo - (resumo.totalPassivo + resumo.totalPL);
        return { ...resumo, liquidez, diff, equilibrado: Math.abs(diff) < 1 };
    }, [dados]);

    const pieData = useMemo(() => {
        if (!stats) return [];
        return [
            { name: 'Ativo',    value: stats.totalAtivo   },
            { name: 'Passivo',  value: stats.totalPassivo },
            { name: 'PL',       value: stats.totalPL      },
        ].filter(d => d.value > 0);
    }, [stats]);

    // ─────────────────────────────────────────────────────────────────────────
    if (loading && !iaLoading) return (
        <div className="bp-loader">
            <div className="bp-loader-inner">
                <div className="bp-loader-ring" />
                <p>Consolidando Exercício Patrimonial…</p>
            </div>
        </div>
    );

    return (
        <div className="bp-wrapper">

            {/* OVERLAY IA */}
            {iaLoading && (
                <div className="bp-ia-overlay">
                    <div className="bp-ia-card">
                        <FaMicrochip className="bp-ia-icon" />
                        <h3>Auditoria Gemini 2.5 Flash</h3>
                        <p>Varrendo estrutura contábil e confrontando lançamentos…</p>
                        <div className="bp-ia-progress"><div className="bp-ia-fill" /></div>
                    </div>
                </div>
            )}

            {/* ── KPI RAIL ──────────────────────────────────────────────────── */}
            {stats && (
                <div className="bp-kpi-rail">
                    <KPICard
                        icon={<FaCalculator />}
                        title="Ativo Total"
                        value={fmtK(stats.totalAtivo)}
                        sub="Bens e Direitos"
                        type="blue"
                    />
                    <KPICard
                        icon={<FaBalanceScale />}
                        title="Liquidez Corrente"
                        value={`${stats.liquidez.toFixed(2)}x`}
                        sub="AC ÷ PC · CPC 26"
                        type="amber"
                    />
                    <KPICard
                        icon={<FaArrowUp />}
                        title="Resultado Exercício"
                        value={fmtK(stats.resultadoExercicio)}
                        sub={stats.resultadoExercicio >= 0 ? 'Lucro Líquido' : 'Prejuízo Líquido'}
                        type={stats.resultadoExercicio >= 0 ? 'green' : 'red'}
                    />
                    <KPICard
                        icon={<FaCheckDouble />}
                        title="Equilíbrio Patrimonial"
                        value={stats.equilibrado ? 'ESTÁVEL' : 'DIVERGENTE'}
                        sub={stats.equilibrado ? 'Partidas dobradas OK' : `Dif: ${fmt(stats.diff)}`}
                        type={stats.equilibrado ? 'stable' : 'alert'}
                    />
                </div>
            )}

            {/* ── TOOLBAR ───────────────────────────────────────────────────── */}
            <header className="bp-toolbar">
                <div className="bp-toolbar-brand">
                    <div className="bp-toolbar-icon">
                        <FaBalanceScale />
                    </div>
                    <div>
                        <h1>Balanço Patrimonial</h1>
                        <p>Consolidação Contábil · Exercício Social {ano}{iaAtiva ? ' · IA Ativa' : ''}</p>
                    </div>
                    {iaAtiva && <div className="bp-ia-badge"><FaRobot /> Gemini</div>}
                </div>
                <div className="bp-toolbar-controls">
                    <button
                        className="bp-btn bp-btn-ia"
                        onClick={() => fetchData(true)}
                        disabled={iaLoading}
                        title="Auditoria completa com Gemini 2.5 Flash"
                    >
                        <FaMicrochip /> Varredura IA
                    </button>
                    <select
                        className="bp-year-select"
                        value={ano}
                        onChange={e => setAno(parseInt(e.target.value))}
                    >
                        {ANOS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                    <button
                        className="bp-btn bp-btn-icon"
                        onClick={() => fetchData(false)}
                        title="Recarregar"
                    >
                        <FaSync />
                    </button>
                    <button
                        className="bp-btn bp-btn-print"
                        onClick={() => handlePrint(dados, ano)}
                        disabled={!dados}
                        title="Imprimir / Exportar PDF"
                    >
                        <FaPrint /> Imprimir
                    </button>
                </div>
            </header>

            {/* ── CORPO PRINCIPAL ───────────────────────────────────────────── */}
            {dados && stats && (
                <div className="bp-body">

                    {/* COLUNA: ATIVOS */}
                    <div className="bp-column">
                        <SectionHeader
                            code="1"
                            title="ATIVOS"
                            total={stats.totalAtivo}
                            variant="ativo"
                            fmt={fmt}
                        />
                        <AccountGroup
                            title="1.1 Ativo Circulante"
                            colorKey="ativo-circ"
                            total={dados.ativos.circulante.total}
                            items={dados.ativos.circulante.detalhes}
                            fmt={fmt}
                        />
                        <AccountGroup
                            title="1.2 Ativo Não Circulante"
                            colorKey="ativo-ncirc"
                            total={dados.ativos.nao_circulante.total}
                            items={dados.ativos.nao_circulante.detalhes}
                            fmt={fmt}
                        />
                        <GrandTotal label="TOTAL DO ATIVO" value={stats.totalAtivo} fmt={fmt} variant="ativo" />
                    </div>

                    {/* COLUNA: PASSIVOS + PL */}
                    <div className="bp-column">
                        <SectionHeader
                            code="2/3"
                            title="PASSIVOS E PL"
                            total={stats.totalPassivo + stats.totalPL}
                            variant="passivo"
                            fmt={fmt}
                        />
                        <AccountGroup
                            title="2.1 Passivo Circulante"
                            colorKey="passivo-circ"
                            total={dados.passivos.circulante.total}
                            items={dados.passivos.circulante.detalhes}
                            fmt={fmt}
                        />
                        <AccountGroup
                            title="2.2 Passivo Não Circulante"
                            colorKey="passivo-ncirc"
                            total={dados.passivos.nao_circulante.total}
                            items={dados.passivos.nao_circulante.detalhes}
                            fmt={fmt}
                        />
                        <AccountGroup
                            title="3. Patrimônio Líquido"
                            colorKey="pl"
                            total={dados.patrimonio_liquido.total}
                            items={dados.patrimonio_liquido.detalhes}
                            fmt={fmt}
                        />
                        <GrandTotal label="TOTAL PASSIVO + PL" value={stats.totalPassivo + stats.totalPL} fmt={fmt} variant="passivo" />
                    </div>

                    {/* SIDEBAR: Gráfico + Parecer IA */}
                    <div className="bp-sidebar">

                        {/* Distribuição de Capital */}
                        <div className="bp-sidebar-card">
                            <div className="bp-card-header">
                                <FaChartPie className="bp-card-icon" />
                                <span>Distribuição de Capital</span>
                            </div>
                            {pieData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            innerRadius={58}
                                            outerRadius={84}
                                            paddingAngle={4}
                                            dataKey="value"
                                        >
                                            {pieData.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => fmt(v)} />
                                        <Legend
                                            verticalAlign="bottom"
                                            iconType="circle"
                                            formatter={(value) => (
                                                <span style={{ fontSize: 12, color: '#64748b' }}>{value}</span>
                                            )}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="bp-empty-chart">Classifique as contas no Plano de Contas para visualizar o gráfico.</div>
                            )}
                        </div>

                        {/* Status de Equilíbrio */}
                        <div className={`bp-status-card ${stats.equilibrado ? 'equilibrado' : 'divergente'}`}>
                            {stats.equilibrado ? (
                                <>
                                    <FaCheckCircle className="bp-status-icon" />
                                    <strong>Balanço Equilibrado</strong>
                                    <p>Ativo = Passivo + PL · Partidas dobradas conferem.</p>
                                </>
                            ) : (
                                <>
                                    <FaExclamationTriangle className="bp-status-icon" />
                                    <strong>Divergência Patrimonial</strong>
                                    <p>Diferença de {fmt(Math.abs(stats.diff))}. Classifique lançamentos no Plano de Contas.</p>
                                </>
                            )}
                        </div>


                    </div>
                </div>
            )}

            {/* ── PARECER IA — full-width abaixo do balanço ───────────── */}
            {iaAtiva && dados && dados.conclusao_ia && (
                <div className="bp-ia-full-card">
                    <div className="bp-ia-full-header">
                        <FaMicrochip style={{ color: '#A78BFA', fontSize: 16 }} />
                        <span>Parecer Técnico · Gemini 2.5 Flash</span>
                        <span className="bp-ia-full-badge">Exercício {ano}</span>
                    </div>
                    <div
                        className="bp-ia-full-content"
                        dangerouslySetInnerHTML={{ __html: dados.conclusao_ia }}
                    />
                </div>
            )}

            {/* Estado vazio */}
            {!loading && !dados && (
                <div className="bp-empty-state">
                    <FaBalanceScale />
                    <p>Nenhum dado disponível para o exercício {ano}.</p>
                </div>
            )}
        </div>
    );
}